import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Bot, Clock, Sparkles, TrendingUp, ArrowRight } from "lucide-react";

const AdminDashboard = () => {
  const [stats, setStats] = useState({ totalPosts: 0, monthPosts: 0, activeAgents: 0, avgTime: 0 });
  const [recentPosts, setRecentPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const [postsRes, monthPostsRes, agentsRes, recentRes] = await Promise.all([
      supabase.from("posts").select("id, generation_time_ms", { count: "exact", head: false }),
      supabase.from("posts").select("id", { count: "exact", head: true }).gte("created_at", monthStart),
      supabase.from("agents").select("id", { count: "exact", head: true }).eq("is_active", true),
      supabase.from("posts").select("id, title, status, created_at, agent_id").order("created_at", { ascending: false }).limit(5),
    ]);

    const times = (postsRes.data || []).map((p: any) => p.generation_time_ms).filter(Boolean);
    const avgTime = times.length > 0 ? times.reduce((a: number, b: number) => a + b, 0) / times.length : 0;

    setStats({
      totalPosts: postsRes.count || 0,
      monthPosts: monthPostsRes.count || 0,
      activeAgents: agentsRes.count || 0,
      avgTime: Math.round(avgTime / 1000),
    });
    setRecentPosts(recentRes.data || []);
    setLoading(false);
  };

  const cards = [
    { label: "Total de Posts", value: stats.totalPosts, icon: FileText, gradient: "from-primary/10 to-primary/5 border-primary/20" },
    { label: "Posts este mês", value: stats.monthPosts, icon: TrendingUp, gradient: "from-emerald-500/10 to-emerald-500/5 border-emerald-500/20" },
    { label: "Agentes Ativos", value: stats.activeAgents, icon: Bot, gradient: "from-violet-500/10 to-violet-500/5 border-violet-500/20" },
    { label: "Tempo médio", value: `${stats.avgTime}s`, icon: Clock, gradient: "from-amber-500/10 to-amber-500/5 border-amber-500/20" },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Visão geral da produção de conteúdo</p>
        </div>
        <Link to="/admin/create">
          <Button className="gap-2 shadow-sm w-full sm:w-auto">
            <Sparkles className="h-4 w-4" /> Gerar Post
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-2xl border bg-gradient-to-br ${c.gradient} p-5 transition-shadow hover:shadow-md`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">{c.label}</span>
              <c.icon className="h-5 w-5 text-muted-foreground/60" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {loading ? <span className="inline-block h-8 w-16 animate-pulse rounded-lg bg-muted" /> : c.value}
            </p>
          </div>
        ))}
      </div>

      {/* Recent Posts */}
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="font-semibold text-foreground">Últimos Posts</h2>
          <Link to="/admin/library" className="text-xs text-primary hover:underline flex items-center gap-1">
            Ver todos <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {loading ? (
          <div className="space-y-3 p-6">{[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}</div>
        ) : recentPosts.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">Nenhum post ainda.</p>
            <Link to="/admin/create">
              <Button variant="outline" size="sm" className="mt-3">Criar primeiro post</Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {recentPosts.map((post) => (
              <Link key={post.id} to={`/admin/posts/${post.id}/edit`} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4 transition-colors hover:bg-muted/30">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground text-sm sm:text-base">{post.title}</p>
                  <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}</p>
                </div>
                <Badge variant={post.status === "published" ? "default" : "secondary"} className={`self-start sm:self-auto shrink-0 ${post.status === "published" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0" : ""}`}>
                  {post.status === "published" ? "Publicado" : "Rascunho"}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
